/**
 * M3.1 UI closure — FirstLoginPasswordGate
 *
 * Modal that intercepts the "must_change_password" state coming from
 * /api/avanzo-b2c/access (detail=password_change_required) when a
 * freshly imported B2B student clicks the Buddy Avanza node.
 *
 * On success:
 *   - POST /api/auth/change-initial-password rotates the credential.
 *   - Backend clears must_change_password, invalidates the temp password,
 *     and records first_password_change_at + audit event.
 *   - We invoke onSuccess() so the parent retries the access probe.
 *
 * No plaintext is persisted client-side. The form clears its state on unmount.
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { X, Lock, ShieldCheck, Loader2 } from "lucide-react";

export default function FirstLoginPasswordGate({ open, onClose, onSuccess }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (next.length < 10) {
      toast.error("La nueva contraseña debe tener al menos 10 caracteres.");
      return;
    }
    if (next !== confirm) {
      toast.error("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    if (next === current) {
      toast.error("La nueva contraseña debe ser distinta a la temporal.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/change-initial-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      // Scrub local state before signaling success.
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Contraseña actualizada. Redirigiendo…");
      onSuccess();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d === "not_on_temp_credential") {
        toast.error("Tu cuenta ya no está en flujo de primer acceso.");
      } else if (d === "credential_expired") {
        toast.error(
          "La contraseña temporal expiró. Solicita a tu institución que la regenere."
        );
      } else if (d === "password_too_short") {
        toast.error("La contraseña debe tener al menos 10 caracteres.");
      } else if (d === "password_common") {
        toast.error("Elige una contraseña menos común.");
      } else if (d === "password_unchanged") {
        toast.error("La nueva contraseña debe ser distinta a la actual.");
      } else if (err?.response?.status === 401) {
        toast.error("La contraseña temporal no coincide.");
      } else {
        toast.error("No se pudo actualizar la contraseña. Intenta de nuevo.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="first-login-gate-overlay"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          disabled={busy}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Cerrar"
          data-testid="first-login-gate-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-orange-100">
            <ShieldCheck className="w-5 h-5 text-orange-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Cambia tu contraseña temporal
          </h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Tu institución te entregó una contraseña temporal para comenzar. Elige
          una nueva para acceder a Buddy Avanza.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="first-login-gate-form">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Contraseña temporal
            </label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="La que te entregó tu institución"
              autoComplete="current-password"
              disabled={busy}
              required
              data-testid="first-login-gate-current"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Nueva contraseña (mínimo 10 caracteres)
            </label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="Nueva contraseña"
              autoComplete="new-password"
              minLength={10}
              disabled={busy}
              required
              data-testid="first-login-gate-new"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Confirmar nueva contraseña
            </label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repite la nueva contraseña"
              autoComplete="new-password"
              minLength={10}
              disabled={busy}
              required
              data-testid="first-login-gate-confirm"
            />
          </div>

          <Button
            type="submit"
            className="w-full mt-2"
            disabled={busy}
            data-testid="first-login-gate-submit"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Actualizando…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Guardar y continuar
              </>
            )}
          </Button>
        </form>

        <p className="text-[10px] text-gray-400 mt-3">
          Contraseña efímera: nunca se almacena en texto plano en nuestro
          sistema. Bcrypt cost 12.
        </p>
      </div>
    </div>
  );
}
